import { useEffect, useState } from "react";
import { Datepicker, locale } from "@mobiscroll/react";
import {
  Box,
  Chip,
  Grid2,
  Grow,
  MenuItem,
  Select,
  TextField,
  useMediaQuery,
  useTheme
} from "@mui/material";
import { Search } from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import { DateTime } from "luxon";
import {
  FetchOrderRequestDto,
  useRemoteFetchOrderRequests
} from "../../../hooks/Remote/Services/OrderRequests/useRemoteFetchOrderRequests";
import { useRemoteFetchOrderRequestDetail } from "../../../hooks/Remote/Services/OrderRequests/useRemoteFetchOrderRequestDetail";
import {
  UpdateServicesDto,
  useRemoteUpdateBookingServicesByAdmin
} from "../../../hooks/Remote/Services/OrderRequests/useRemoteUpdateBookingServicesByAdmin";
import { useRemotePatchActiveBookingRequests } from "../../../hooks/Remote/BookingRequests/useRemotePatchActiveBookingRequests";
import {
  OrderRequestData,
  DetailedServiceBookingData,
  PlaceOrZoneType
} from "./Domain/RemoteOrderRequests.types";
import { SortOrder } from "./Domain/OrderRequestList.types";
import {
  ServiceDeliveryStatus,
  ServiceType
} from "../../../features/Booking-Form/typings/booking-service-administration.types";
import useTimeFormat from "../../../hooks/useTimeFormat/useTimeFormat";
import { filterOrderRequestData } from "./functions/filterOrderRequestData.function";
import {
  countOrderRequestCateringFilterItems,
  countOrderRequestEquipmentFilterItems
} from "./functions/orderRequestServiceTypeFilter.function";
import { OrderRequestListContents } from "./OrderRequestListContents.component";
import { OrderRequestCardItem } from "./partial/OrderRequestCardItem.partial";
import { OrderRequestDetailDialog } from "./partial/OrderRequestDetailDialog.partial";
import useServiceTypeColor from "./hooks/useServiceTypeColor";
import { applyServiceId } from "./functions/applyServiceId.function";
import { enrichResourceEquipmentDetail } from "./functions/enrichResourceEquipmentDetail.function";
import { finalizeOrderRequestEquipmentDetail } from "./functions/normalizeOrderRequestDetailData.function";
import { transformOrderRequest } from "./functions/transformOrderRequest.function";
import { patchAssociatedBookingApproval } from "./functions/patchAssociatedBookingApproval.function";
import { resolveOrderRequestBookingType } from "./functions/resolveOrderRequestBookingType.function";
import { BookingRequestsStatus } from "../../BookingRequest/typings/booking-requests.types";

export const DateSortOrder = {
  DateAscending: SortOrder.DateAscending,
  DateDescending: SortOrder.DateDescending
} as const;

export function OrderRequestList() {
  const { t, i18n } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isTablet = useMediaQuery(theme.breakpoints.between("sm", "md"));

  const { dateFormat } = useTimeFormat();
  const initialDate = {
    dateFrom: DateTime.local().toFormat("yyyy-MM-dd"),
    dateTo: DateTime.local().plus({ days: 30 }).toFormat("yyyy-MM-dd")
  };

  const [data, setData] = useState<OrderRequestData[]>([]);
  const [filteredData, setFilteredData] = useState<OrderRequestData[]>([]);
  const [detailData, setDetailData] = useState<DetailedServiceBookingData | null>(null);

  const [selectDate, setSelectDate] = useState<FetchOrderRequestDto>(initialDate);
  const [sortOrder, setSortOrder] = useState<SortOrder>(SortOrder.DateAscending);
  const [filterByType, setFilterByType] = useState<ServiceType[]>(Object.values(ServiceType));
  const [searchText, setSearchText] = useState("");
  const [selectedOrder, setSelectedOrder] = useState<OrderRequestData | null>(null);

  const [isDetailDialogOpen, setIsDetailDialogOpen] = useState(false);

  const {
    data: orderRequestData,
    isFetching: orderRequestFetching,
    refetch: refetchOrderRequests
  } = useRemoteFetchOrderRequests(selectDate);

  const { mutateAsync: fetchDetail, isLoading: isDetailLoading } =
    useRemoteFetchOrderRequestDetail();
  const { mutateAsync: postOrderRequest, isLoading: isPostLoading } =
    useRemoteUpdateBookingServicesByAdmin();
  const { mutateAsync: patchBookingRequest } = useRemotePatchActiveBookingRequests();

  const { cateringColor, equipmentColor, layoutColor } = useServiceTypeColor(filterByType);
  const usedCateringNumber = countOrderRequestCateringFilterItems(data);
  const usedEquipmentNumber = countOrderRequestEquipmentFilterItems(data);
  const usedRoomLayoutNumber = data
    .filter(item => item.numberOfRoomLayouts > 0)
    .reduce((sum, item) => sum + item.numberOfRoomLayouts, 0);
  const noUsedServiceItems =
    usedCateringNumber === 0 && usedEquipmentNumber === 0 && usedRoomLayoutNumber === 0;

  const closeDialog = () => {
    setIsDetailDialogOpen(false);
    setDetailData(null);
  };

  const handleClickFilterByType = (select: ServiceType) => {
    if (filterByType.includes(select)) {
      // select removed
      setFilterByType(filterByType.filter(type => type !== select));
    } else {
      // select added
      setFilterByType(prev => [...prev, select]);
    }
  };

  const handleUpdateOrderRequest = async (
    targetBookingId: string,
    changedDeliveryStatus: ServiceDeliveryStatus,
    changes: Pick<DetailedServiceBookingData, "caterings" | "equipments" | "roomLayouts">
  ) => {
    const updated = transformOrderRequest(
      targetBookingId,
      changedDeliveryStatus,
      resolveOrderRequestBookingType(detailData?.placeOrZoneType ?? PlaceOrZoneType.Place),
      changes
    );

    handlePostOrderRequest(updated);
  };

  const handlePostOrderRequest = async (updated: UpdateServicesDto[]) => {
    await postOrderRequest(updated).then(() => {
      setIsDetailDialogOpen(false);
      refetchOrderRequests();
    });
  };

  const handleOpenDetail = (bookingId: string) => {
    /** istanbul ignore next */
    if (Number.isNaN(bookingId)) return;

    fetchDetail(Number(bookingId)).then(async data => {
      const listRow =
        selectedOrder?.bookingId === bookingId || selectedOrder?.bookingId === String(bookingId)
          ? selectedOrder
          : null;
      const normalized = applyServiceId(data, listRow, { finalizeEquipmentDefaults: false });
      const enriched = await enrichResourceEquipmentDetail(normalized);
      setDetailData(finalizeOrderRequestEquipmentDetail(enriched));
      setIsDetailDialogOpen(true);
    });
  };

  const handleApproveAssociatedBooking = async () => {
    await patchAssociatedBookingApproval(detailData, patchBookingRequest);
  };

  useEffect(() => {
    if (!orderRequestData?.length) {
      setData([]);
      setFilteredData([]);
      setSelectedOrder(null);
      return;
    }

    setData(orderRequestData);
    setFilteredData(orderRequestData);

    // select the latest order at first
    if (selectedOrder === null) {
      const latestOrder = orderRequestData.toSorted(
        (a, b) => DateTime.fromISO(b.date).valueOf() - DateTime.fromISO(a.date).valueOf()
      )[0];

      setSelectedOrder(latestOrder);
    }
  }, [orderRequestFetching]);

  useEffect(() => {
    if (!data.length) return;

    // apply filter : filter by type, sort order, search text
    const result = filterOrderRequestData({
      data,
      searchText,
      filterByType,
      sortOrder
    });
    setFilteredData(result);
  }, [filterByType, sortOrder, searchText, selectDate, data]);

  return (
    <>
      <Box
        data-testid="order-request-list-parent-box"
        sx={{
          display: "flex",
          height: "100%",
          width: "100%",
          overflowX: isMobile || isTablet ? "auto" : undefined,
          // Adjust scrollbar height in Webkit browsers (Chrome, Safari)
          "&::-webkit-scrollbar": { height: "5px" }
        }}
      >
        {/* list of order requests */}
        <Grid2
          container
          // md={9}
          data-testid="order-request-list-parent"
          sx={{
            minWidth: "auto",
            display: "flex",
            width: "100%",
            height: "100%",
            overflowX: isMobile || isTablet ? undefined : "auto"
          }}
        >
          <Box
            data-testid="order-request-list-header"
            sx={[
              {
                display: "flex",
                width: "100%",
                height: "fit-content",
                alignItems: "center",
                justifyContent: "space-between"
              },
              isMobile ? { flexDirection: "column" } : { flexDirection: "row" }
            ]}
          >
            {/* service type filter chips */}
            <Box sx={[{ display: "flex" }, isMobile ? { order: 1 } : { order: 0 }]}>
              {usedCateringNumber > 0 && (
                <Chip
                  sx={{ mx: 0.3, backgroundColor: cateringColor, color: "black" }}
                  label={t("OrderRequest_Catering", { number: usedCateringNumber })}
                  clickable
                  data-testid="filterByType-chip-Catering"
                  onClick={() => handleClickFilterByType(ServiceType.Catering)}
                />
              )}
              {usedEquipmentNumber > 0 && (
                <Chip
                  sx={{ mx: 0.3, backgroundColor: equipmentColor, color: "black" }}
                  label={t("OrderRequest_Equipment", { number: usedEquipmentNumber })}
                  clickable
                  data-testid="filterByType-chip-Bookable_Equipment"
                  onClick={() => handleClickFilterByType(ServiceType.Bookable_Equipment)}
                />
              )}
              {usedRoomLayoutNumber > 0 && (
                <Chip
                  sx={{ mx: 0.3, backgroundColor: layoutColor, color: "black" }}
                  label={t("OrderRequest_Layout", { number: usedRoomLayoutNumber })}
                  clickable
                  data-testid="filterByType-chip-Room_Layout"
                  onClick={() => handleClickFilterByType(ServiceType.Room_Layout)}
                />
              )}

              {/* no service items used */}
              {noUsedServiceItems && (
                <Chip
                  sx={{
                    mx: 0.3,
                    backgroundColor: theme.palette.background.paper,
                    color: theme.palette.text.primary
                  }}
                  label={t("No result")}
                  clickable={false}
                />
              )}
            </Box>
            {/* sort part */}
            <Box
              sx={[
                { display: "flex", alignItems: "center" },
                isMobile ? { alignSelf: "flex-end" } : { alignSelf: "unset" },
                isMobile ? { order: 0 } : { order: 1 }
              ]}
            >
              <Select
                value={sortOrder}
                variant={"outlined"}
                data-testid="order-request-list-sort-select"
                onChange={e => setSortOrder(e.target.value as SortOrder)}
                sx={{
                  "& .MuiOutlinedInput-notchedOutline": { border: "none" },
                  "&:hover .MuiOutlinedInput-notchedOutline": { border: "none" },
                  "&.Mui-focused .MuiOutlinedInput-notchedOutline": { border: "none" },
                  backgroundColor: "transparent", // Optional
                  boxShadow: "none", // Optional,
                  fontSize: 15
                }}
                SelectDisplayProps={{
                  style: { paddingTop: 4, paddingBottom: 4, paddingRight: 32, paddingLeft: 0 }
                }}
              >
                {Object.entries(DateSortOrder).map(sort => (
                  <MenuItem value={sort[1]} key={sort[1]}>
                    {t(`OrderRequestBoardSort_${sort[0]}`)}
                  </MenuItem>
                ))}
              </Select>
            </Box>
          </Box>

          {/* list part */}
          <Grid2
            sx={{
              width: "100%",
              height: "70vh",
              mt: 1,
              mb: 1,
              overflowX: "overlay",
              overflowY: "hidden",
              "&::-webkit-scrollbar": {
                display: "none"
              }
            }}
            data-testid="order-request-list-part"
          >
            <OrderRequestListContents
              data={filteredData}
              setFilteredData={setFilteredData}
              setSelectedOrder={setSelectedOrder}
              filterByType={filterByType}
              refetchOrderRequests={refetchOrderRequests}
            />
          </Grid2>
        </Grid2>

        {/* filter - search field, calendar and detail part of order request  */}
        <Grid2
          container
          data-testid="order-request-filter-detail-parent"
          sx={{
            display: "block",
            px: 3,
            mt: "12px",
            width: "fit-content",
            minWidth: "auto"
          }}
        >
          <Box sx={{ display: "flex", justifyContent: "space-between", mb: 2.5 }}>
            <TextField
              placeholder={t("Search")}
              data-testid="order-request-search-field"
              slotProps={{ input: { startAdornment: <Search sx={{ mr: 1 }} /> } }}
              onChange={event => setSearchText(event.target.value.toLowerCase())}
              sx={{ width: "95%" }}
            />
            {/* file download button - not used yet */}
            {/* <IconButton> <FileDownload /> </IconButton> */}
          </Box>

          {/* date picker filter */}
          <Box>
            <Datepicker
              display={"inline"}
              select="range"
              data-testid="order-request-list-datePicker"
              rangeHighlight={true}
              dateFormat={dateFormat.toUpperCase()}
              locale={locale[i18n.language]}
              value={[selectDate.dateFrom, selectDate.dateTo]}
              onChange={prop => {
                const selectedDate = prop.value as (Date | null)[];

                setSelectDate({
                  dateFrom: DateTime.fromJSDate(selectedDate[0] as Date).toFormat("yyyy-MM-dd"),
                  dateTo: DateTime.fromJSDate(selectedDate[1] as Date).toFormat("yyyy-MM-dd")
                });

                if (!selectedDate.every(date => date !== null)) return;

                // wait for selectDate state update and then refetch
                setTimeout(() => /** istanbul ignore next */ {
                  refetchOrderRequests();
                }, 10);
              }}
            />
          </Box>

          {/* selected order request item box */}
          <Box data-testid="selected-order-box" sx={{ mt: 3 }}>
            <Grow
              in={!!selectedOrder}
              mountOnEnter
              unmountOnExit
              key={selectedOrder?.id}
              {...(selectedOrder ? { timeout: 1000 } : {})}
            >
              <Box>
                {selectedOrder !== null && (
                  <OrderRequestCardItem
                    item={selectedOrder}
                    isSelected
                    filterByType={filterByType}
                    handleOpenDetail={handleOpenDetail}
                    isDetailLoading={isDetailLoading}
                    isArchive={DateTime.fromISO(selectedOrder.date).diffNow().valueOf() <= 0}
                    isBookingCancelled={selectedOrder.status === BookingRequestsStatus.CANCELLED}
                  />
                )}
              </Box>
            </Grow>
          </Box>
        </Grid2>
      </Box>
      {/* detail dialog of the selected order request */}
      {selectedOrder !== null && detailData !== null && (
        <OrderRequestDetailDialog
          key={selectedOrder.id}
          isDetailDialogOpen={isDetailDialogOpen}
          closeDialog={closeDialog}
          selectedOrder={selectedOrder}
          detailData={detailData}
          updateOrderRequest={(targetBookingId, changedDeliveryStatus, changes) =>
            handleUpdateOrderRequest(targetBookingId, changedDeliveryStatus, changes)
          }
          approveAssociatedBooking={handleApproveAssociatedBooking}
          isArchive={DateTime.fromISO(selectedOrder.date).diffNow().valueOf() <= 0}
          isBookingCancelled={selectedOrder.status === BookingRequestsStatus.CANCELLED}
          isPostLoading={isPostLoading}
        />
      )}
    </>
  );
}
